import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { HxAccordion } from '../accordion/accordion';
import { HxAccordionPanel } from '../accordion/accordion-panel';
import { HxButton } from '../button/button';
import { HxDialogRef } from '../dialog/dialog-ref';
import { HxDialogComponent } from '../dialog/dialog.component';
import { DIALOG_REF } from '../dialog/internal/dialog-injectors';
import { HxIcon } from '../icon/icon';
import type { HxIconCategory, HxIconPickerIcon } from './icon-picker.models';

@Component({
  selector: 'hx-icon-picker-dialog',
  standalone: true,
  imports: [CommonModule, HxAccordion, HxAccordionPanel, HxButton, HxDialogComponent, HxIcon],
  templateUrl: './icon-picker-dialog.html',
  styleUrls: ['./icon-picker-dialog.css', './icon-picker-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
/**
 * Dialog component for browsing and selecting icons from categorized lists.
 *
 * Used internally by HxIconPicker. Displays a searchable, categorized view of icons with accordion groups and real-time
 * filtering.
 */
export class HxIconPickerDialogComponent implements OnInit {
  /** Dialog title text shown in the header. */
  readonly title = input<string>('Add Icons');
  /** Placeholder text for the search input. */
  readonly searchPlaceholder = input<string>('Search Icons');
  /** Empty state message shown when no icons match the search. */
  readonly emptyStateText = input<string>('No icons found');
  /** Icon categories to display, grouped by name and containing icon metadata. */
  readonly categories = input<HxIconCategory[]>([]);
  /** Currently selected icon name, if any. */
  readonly selectedIcon = input<string | null>(null);
  /** Default icon name used when no selection exists. */
  readonly defaultIcon = input<string>('bounding-box');

  private readonly dialogRef = inject<HxDialogRef>(DIALOG_REF);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchInput$ = new Subject<string>();

  protected readonly searchQuery = signal<string>('');
  protected readonly activeSelection = signal<string | null>(null);
  protected readonly expandedCategories = signal<Set<string>>(new Set());
  protected readonly manualExpanded = signal<Set<string>>(new Set());

  protected readonly searchBarWidth = computed(() => {
    const placeholder = this.searchPlaceholder();
    const charWidth = 7.5;
    const iconAndPadding = 52;
    const needed = Math.ceil(placeholder.length * charWidth) + iconAndPadding;
    const defaultWidth = 280;
    return needed > defaultWidth ? `${needed}px` : null;
  });

  private selectionInitialized = false;

  private readonly initializeSelection = effect(() => {
    if (this.selectionInitialized) {
      return;
    }
    const initial = this.selectedIcon() || this.defaultIcon();
    this.activeSelection.set(initial || null);
    this.selectionInitialized = true;
  });

  private readonly syncExpandedCategories = effect(() => {
    const categories = this.categories();
    const query = this.searchQuery().trim();

    if (query.length === 0) {
      const available = new Set(categories.map((category) => category.name));
      const manualCurrent = this.manualExpanded();
      const manualFiltered = new Set([...manualCurrent].filter((name) => available.has(name)));

      if (!this.setsEqual(manualFiltered, manualCurrent)) {
        this.manualExpanded.set(manualFiltered);
      }

      if (!this.setsEqual(manualFiltered, this.expandedCategories())) {
        this.expandedCategories.set(new Set(manualFiltered));
      }
      return;
    }

    const expandedTarget = new Set(categories.map((category) => category.name));
    if (!this.setsEqual(expandedTarget, this.expandedCategories())) {
      this.expandedCategories.set(expandedTarget);
    }
  });

  protected readonly filteredCategories = computed<HxIconCategory[]>(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const categories = this.categories();

    const mapped = categories.map((category) => {
      const icons = this.filterAndSortIcons(category.icons, query);
      return { name: category.name, icons };
    });

    return mapped.filter((category) => category.icons.length > 0).sort((a, b) => a.name.localeCompare(b.name));
  });

  ngOnInit(): void {
    this.searchInput$
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        this.searchQuery.set(query);
      });
  }

  protected onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchInput$.next(target.value ?? '');
  }

  protected isCategoryExpanded(categoryName: string): boolean {
    return this.expandedCategories().has(categoryName);
  }

  protected onCategoryOpened(categoryName: string): void {
    const expanded = new Set(this.expandedCategories());
    expanded.add(categoryName);
    this.expandedCategories.set(expanded);

    if (this.searchQuery().trim().length === 0) {
      this.manualExpanded.set(new Set(expanded));
    }
  }

  protected onCategoryClosed(categoryName: string): void {
    const expanded = new Set(this.expandedCategories());
    expanded.delete(categoryName);
    this.expandedCategories.set(expanded);

    if (this.searchQuery().trim().length === 0) {
      this.manualExpanded.set(new Set(expanded));
    }
  }

  protected selectIcon(icon: string): void {
    this.activeSelection.set(icon);
  }

  protected isSelected(icon: string): boolean {
    return this.activeSelection() === icon;
  }

  protected confirmSelection(): void {
    const selected = this.activeSelection() || this.defaultIcon();
    this.dialogRef.close(selected || null);
  }

  protected cancelSelection(): void {
    this.dialogRef.close(null);
  }

  private filterAndSortIcons(icons: HxIconPickerIcon[], query: string): HxIconPickerIcon[] {
    const filtered = query ? icons.filter((icon) => icon.name.toLowerCase().includes(query)) : icons.slice();

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }

  private setsEqual(left: Set<string>, right: Set<string>): boolean {
    if (left.size !== right.size) {
      return false;
    }

    for (const value of left) {
      if (!right.has(value)) {
        return false;
      }
    }

    return true;
  }
}
